import { EventImpl } from '@fullcalendar/core/internal';
import FullCalendar from "./full-calendar";
import Form from './form';
import DurationInput from './duration-input';

export interface ReviewData {
  items: [
    {
      id: string,
      title: string,
      price: string
    }
  ],
  strings: {
    day: string,
    timerange: string,
    duration: string,
    sum: string,
    dph: string,
    sumDph: string
  }
}

export interface ErrorData {
  [name: string]: {
    label: string;
    message: {
      [errorType: string]: string
    };
  };
}

export default class ReservationForm extends Form
{
  protected readonly clickHandler: EventListener;
  protected readonly changeHandler: EventListener;
  protected readonly keyUpHandler: EventListener;
  protected readonly submitHandler: EventListener;
  protected readonly slotSelectHandler: EventListener;

  private readonly csrf: string;
  private readonly navButtons: NodeListOf<HTMLButtonElement>;
  private readonly steps: Array<HTMLElement> = [];
  private readonly stepConfiguration: HTMLElement;
  private readonly stepDate: HTMLElement;
  private readonly stepReview: HTMLElement;

  private slot: EventImpl|null = null;
  private currentStep: HTMLElement;
  private calendar: FullCalendar;
  private durationInput: DurationInput;

  constructor(form: HTMLFormElement)
  {
    super(form);

    this.navButtons = this.form.querySelectorAll('#form-navigation button') as NodeListOf<HTMLButtonElement>;

    const stepConfiguration = document.getElementById('step-configuration');
    if(stepConfiguration instanceof HTMLElement) {
      this.steps.push(stepConfiguration);
      this.stepConfiguration = stepConfiguration;
    } else {
      throw new Error("Configuration step not found");
    }
    
    const stepDate = document.getElementById('step-date');
    if(stepDate instanceof HTMLElement) {
      this.steps.push(stepDate);
      this.stepDate = stepDate;
    } else {
      throw new Error("Date step not found");
    }
    const stepReview = document.getElementById('step-review');
    if(stepReview instanceof HTMLElement) {
      this.steps.push(stepReview);
      this.stepReview = stepReview;
    } else {
      throw new Error("Review step not found");
    }

    const durationInputWrapper = this.form.querySelector('.duration-input-wrapper');
    if(durationInputWrapper instanceof HTMLElement) {
      this.durationInput = new DurationInput(durationInputWrapper);
    }

    const csrfInput = this.form.querySelector('input[name=csrf]');
    if(csrfInput instanceof HTMLInputElement) {
      this.csrf = csrfInput.value;
    } else {
      throw new Error("Csrf input is not present");
    }

    this.currentStep = this.stepConfiguration;
    this.updateNavigation();
    
    this.clickHandler = this.handleClickEvent.bind(this);
    this.keyUpHandler = this.handleKeyUpEvent.bind(this);
    this.submitHandler = this.handleSubmitEvent.bind(this);
    this.slotSelectHandler = this.handleSlotSelectEvent.bind(this);
    this.changeHandler = this.handleChangeEvent.bind(this);

    this.enable();
  }

  public enable(): void
  {
    super.enable();
    this.form.addEventListener('change', this.changeHandler, true);
    this.form.addEventListener('click', this.clickHandler, false);
    this.form.addEventListener('keydown', this.keyUpHandler, false);
    this.form.addEventListener('submit', this.submitHandler, false);
    this.form.addEventListener('slot-select', this.slotSelectHandler, false);

    this.calendar = new FullCalendar();

    this.currentStep.querySelectorAll('input,select,textarea').forEach(field => {
      field.dispatchEvent(new Event('change'));
    })

    this.resetForm();
  }

  private async handleSubmitEvent(event: SubmitEvent): Promise<void>
  {
    event.preventDefault();

    this.setLoading(true);
    const formData = await this.getFormData();
    this.setDisabled(true);

    fetch(this.form.action, {
      method: 'post',
      body: formData,
      headers: {
        'Csrf-Token': this.csrf
      }
    }).then((response) => {

      if(!response.ok) {
        throw new Error();
      }
      return response.json();

    }).then((data) => {

      if(data.details) {
        this.setLoading(false);
        this.setDisabled(false);
        this.showErrors(data.details);
      } else if (data.redirect) {
        //see ya!
        window.location.href = data.redirect;
      }
    }).catch((reason) => {
      this.showAlert('Omlouváme se, stala se neočekávaná chyba');
      this.setDisabled(false);
      this.setLoading(false);
    })
  }

  protected handleChangeEvent(event: Event): void
  {
    super.handleChangeEvent(event);
    if(event.target instanceof HTMLInputElement) {

      this.selectAndDisableIncludedOptions(event.target)

      if(
        event.target.name == 'resource-main'
        && event.target.checked
      ) {
        this.durationInput.setResource(event.target.value);
      }
    }
  }

  private selectAndDisableIncludedOptions(changedInput: HTMLInputElement): void
  {
    const checkedInputsWithIncludedOptions = this.form.querySelectorAll('input:checked[data-include]') as NodeListOf<HTMLInputElement>;
    const allIncludedOptions: string[] = [];

    checkedInputsWithIncludedOptions.forEach(input => {
      const includedOptions = input.dataset.include?.split(' ') ?? [];
      allIncludedOptions.push(...includedOptions);
    });

    this.form.querySelectorAll('input').forEach(input => {
      if(input instanceof HTMLInputElement) {
        const isMainResource = (input.name == "resource-main");
        const isIncluded = (allIncludedOptions.indexOf(input.value) != -1)
        const isAlreadySelected = input.checked;
        const shouldBeSetAsIncluded = (
          isIncluded
        );
        if(!isMainResource && input != changedInput) {
          this.setInputAsIncluded(input, shouldBeSetAsIncluded);
        }
      }
    });
  }

  private handleKeyUpEvent(event: KeyboardEvent): void
  {
    if(
      event.key == 'Enter'
      && document.activeElement?.id == 'discount'
    ) {
      const discountInput = document.activeElement as HTMLInputElement;
      if(discountInput.value) {
        this.addDiscount();
      }
    }
  }

  private handleSlotSelectEvent(event: CustomEventInit<EventImpl|null>): void
  {
    const slot = event.detail;

    if(slot instanceof EventImpl) {
      this.slot = slot;
      //simulate validation
      this.form.querySelectorAll('.field-calendar .error-message').forEach((errorMessage) => {
        this.hideErrorMessage(errorMessage);
      })
    } else {
      this.slot = null;
    }
    // this.updateReview();
  }

  private handleClickEvent(event: MouseEvent): void
  {
    if(
      event.target instanceof HTMLButtonElement
    ) {
      const target = event.target as HTMLButtonElement;

      switch(true) {
        case event.target.classList.contains('button-next-step'):
        case event.target.classList.contains('button-prev-step'):
        case event.target.classList.contains('button-jump-step'):

          const targetStep = this.steps.find((step) => {
            return step.id == target.dataset.target
          })
    
          if(targetStep) {
            this.showStep(targetStep);
          }
          break;

        case event.target.id == 'add-discount':
          this.addDiscount();
          break;
      }
    }
  }

  private async validateStep(): Promise<boolean>
  {
    this.setLoading(true);
    const formData = await this.getFormData();
    this.setDisabled(true);

    const isValid = await fetch('/validate', {
      method: 'post',
      body: formData,
      headers: {
        'Csrf-Token': this.csrf
      }
    }).then((response) => {

      this.setLoading(false);

      if(!response.ok) {
        throw new Error();
      }
      return response.json();

    }).then((data) => {

      if(data.code && data.code >= 300) {

        if(typeof data.details == 'object') {
          this.showErrors(data.details);
        } else {
          throw new Error(data.message);
        }
        return false;

      } else {

        //is valid
        if(data.action) {
          this.form.action = data.action;
        }
        if(typeof data.minDuration == 'number') {
          this.calendar.setMinDuration(data.minDuration);
        }
        if(typeof data.maxDuration == 'number') {
          this.calendar.setMaxDuration(data.maxDuration);
        }
        if(typeof data.businessHours == 'object') {
          this.calendar.setBusinessHours(data.businessHours);
        }
        if(typeof data.slotMinTime == 'string') {
          this.calendar.setSlotMinTime(data.slotMinTime);
        }
        if(typeof data.slotMaxTime == 'string') {
          this.calendar.setSlotMaxTime(data.slotMaxTime);
        }
        if(data.events) {
          this.calendar.setEvents(data.events);
        }
        if(data.cart) {
          this.updateCart(data.cart);
        }
        return true;
      }
    }).catch((reason) => {

      this.showAlert('Omlouváme se, stala se neočekávaná chyba');
      return false;

    }).finally(() => {
      this.setDisabled(false);
    });
    return isValid;
  }

  private async showStep(newStep: HTMLElement, doValidation: boolean = true): Promise<boolean>
  {
    const currentStepIndex = this.steps.indexOf(this.currentStep);
    const targetStepIndex = Math.min(
      currentStepIndex+1, // cap stepping to one step at a time
      this.steps.indexOf(newStep)
    );

    if(currentStepIndex > targetStepIndex) {
      //going back, no validation?
      doValidation = false;
    }

    if(newStep instanceof HTMLElement && newStep != this.currentStep) {

      //start process show loading overlay
      this.setLoading(true);
      //do validation if needed
      let isValid = doValidation === true ? await this.validateStep() : true;

      if(isValid) {

        //init calendar
        if(newStep.id == 'step-date') {
          this.calendar.enable();
        }

        this.form.scrollIntoView();
        this.form.classList.add('is-changing'); //hide all content

        return new Promise(resolve => setTimeout(() => {

          this.currentStep.classList.add('hidden');
          this.currentStep = newStep;
          this.currentStep.classList.remove('hidden');

          this.updateNavigation(); //based on currentstep
  
          //recalc of various elements that need it
          switch(this.currentStep.id) {
            case 'step-date':
              const durationPreset = this.durationInput.getValue();
              this.calendar.setDurationPreset(durationPreset)
              this.calendar.calendar?.render();
              break;
            case 'step-review':
              // this.form.querySelectorAll('textarea').forEach(this.resizeTextArea);
              this.textareas.forEach(textarea => textarea.resize());
              break;
          }

          //destroy calendar if not needed
          //this is important, calendar is broken when out of viewport
          //freezing after ~30 seconds after step change
          if(newStep.id !== 'step-date') {
            this.calendar.destroy();
          }

          document.body.dataset.currentStep = this.currentStep.id; //not sure if needed
          this.setDisabled(false);
          this.setLoading(false);
          this.form.classList.remove('is-changing'); //show content

          resolve(true);
        }, 500));
      }
    }
    return false;
  }

  private async addDiscount(): Promise<boolean>
  {
    this.setLoading(true);
    const formData = await this.getFormData();
    this.setDisabled(true);

    //empty input
    const discountInput = this.form.querySelector('#discount')
    if(discountInput instanceof HTMLInputElement) {
      discountInput.value = '';
      discountInput.dispatchEvent(new Event('change'));
    }

    const isValid = await fetch('/add-discount', {
      method: 'post',
      body: formData,
      headers: {
        'Csrf-Token': this.csrf
      }
    }).then((response) => {

      this.setLoading(false);

      if(!response.ok) {
        throw new Error();
      }
      return response.json();

    }).then((data) => {

      if(data.code && data.code >= 300) {

        if(typeof data.details == 'object') {
          this.showErrors(data.details);
        } else {
          throw new Error(data.message);
        }
        return false;

      } else {

        //is valid
        if(data.cart) {
          this.updateCart(data.cart);
        }
        return true;
      }

    }).catch((reason) => {

      this.showAlert('Omlouváme se, stala se neočekávaná chyba');
      return false;

    }).finally(() => {
      this.setDisabled(false);
    });
    return isValid;
  }

  private showAlert(message: string): void
  {
    //TODO hezčí?
    alert(message)
  }

  private showErrors(errorsData: ErrorData): void
  {
    //remove all previous errors
    // this.form.querySelectorAll('.error-message').forEach((elem) => elem.remove());

    //show new errors
    Object.keys(errorsData).forEach((fieldName) => {

      const errorData = errorsData[fieldName];
      let fieldWrapper: Element | null = null;

      switch (fieldName) {

        case 'calendar':
          this.calendar.getCalendar().getEventById('selection')?.remove();
          // fieldWrapper = this.form.querySelector('#' + fieldName)

      
        default:
          
          Object.keys(errorData.message).forEach((validatorType) => {

            fieldWrapper = this.form.querySelector('.field-' + fieldName);

            const prevErrorMessageWrapper = fieldWrapper?.querySelector('.error-message');
            if(prevErrorMessageWrapper) {
              prevErrorMessageWrapper.remove();
            }

            const errorMessageWrapper = document.createElement('div');
            errorMessageWrapper.classList.add('error-message');

            const errorMessage = document.createElement('p');
            errorMessage.textContent = errorData.message[validatorType];

            errorMessageWrapper.append(errorMessage)

            fieldWrapper?.querySelectorAll('input').forEach((input) => {
              input?.classList.add('invalid');
            })

            if(fieldWrapper?.classList.contains('fieldset')) {
              fieldWrapper?.querySelector('.option-list')?.prepend(errorMessageWrapper);
            } else {
              fieldWrapper?.append(errorMessageWrapper);
            }
          })

          //change step / scroll to error
          const firstErrorMessage = document.querySelector('.error-message');
          const firstErrorMessageStep = firstErrorMessage?.closest('.form-step');
          if(
            firstErrorMessage instanceof HTMLElement
            && firstErrorMessageStep instanceof HTMLElement
            && this.steps.indexOf(firstErrorMessageStep) !== -1
            && this.steps.indexOf(firstErrorMessageStep) < this.steps.indexOf(this.currentStep)
          ) {
            this.showStep(firstErrorMessageStep).then(() => {
              firstErrorMessage.parentElement?.scrollIntoView()
            });
          } else {
            firstErrorMessage?.parentElement?.scrollIntoView();
          }
          break;
      }
    })
  }

  private async getFormData(): Promise<FormData>
  {
    const formData = new FormData(this.form);

    if(this.slot && this.slot.start && this.slot.end) {
      formData.append('start', this.slot.start.toISOString());
      formData.append('end', this.slot.end.toISOString());
    }
    formData.append('step-id', this.currentStep.id);

    return formData;
  }

  private setInputAsIncluded(input: HTMLInputElement, isIncluded: boolean): void
  {
    if(isIncluded) {
      input.checked = false;
    }

    input.toggleAttribute('disabled', isIncluded);
    input.parentElement?.classList.toggle('is-included', isIncluded);
  }

  private resetForm(): void
  {
    this.form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      if(input instanceof HTMLInputElement) {
        const isDefault = input.hasAttribute('checked');
        input.checked = isDefault;
      }
    });

    this.calendar?.calendar?.getEventById('selection')?.remove();
  }

  private updateCart(data: ReviewData | null): void
  {
    const reviewElements = document.querySelectorAll('.order-review');

    if(!data) {
      reviewElements.forEach(element => element.classList.add('empty'));
      return;
    }

    this.calendar.setReviewData(data as ReviewData)

    document.querySelectorAll('.review-day').forEach(element => {
      element.textContent = data.strings.day;
    });

    document.querySelectorAll('.review-timerange').forEach(element => {
      element.textContent = data.strings.timerange;
    });

    document.querySelectorAll('.review-duration').forEach(element => {
      element.textContent = data.strings.duration;
    })

    document.querySelectorAll('.cart tbody').forEach(tableBody => {
      
      tableBody.querySelectorAll('tr').forEach(row => row.remove());

      for (let index = 0; index < data.items.length; index++) {
        const itemData = data.items[index];
        const row = document.createElement('tr');

        const titleCell = document.createElement('td');

        if(index == 0) {
          titleCell.innerHTML = `
            <p>` + itemData.title + `</p>
            <small>` + data.strings.duration + `</small>
            <small>` + data.strings.day + `</small>
            <small>` + data.strings.timerange + `</small>
          `
        } else {
          titleCell.textContent = itemData.title;
        }

        const priceCell = document.createElement('td');
        priceCell.textContent = itemData.price;

        row.appendChild(titleCell);
        row.appendChild(priceCell)
        tableBody.appendChild(row);
      }
    })

    document.querySelectorAll('.review-sum').forEach(element => {
      element.textContent = data.strings.sum;
    })
    document.querySelectorAll('.review-dph').forEach(element => {
      element.textContent = data.strings.dph;
    })
    document.querySelectorAll('.review-sum-dph').forEach(element => {
      element.textContent = data.strings.sumDph;
    })

    reviewElements.forEach(element => element.classList.remove('empty'));
  }

  private updateNavigation()
  {
    let isActiveButtonFound = false;
    
    this.navButtons.forEach((navButton) => {

      navButton.toggleAttribute('aria-current', false);

      if(navButton.dataset.target == this.currentStep.id) {
        isActiveButtonFound = true;
        navButton.setAttribute('aria-current','step');
      }
      navButton.toggleAttribute('disabled', isActiveButtonFound);
    })
  }
}